import { assignJobToVehicle } from './mutatePlan';
import type { DailySchedulePlan } from './types';
import type { ScheduleValidationIssue } from './validationIssues';

export interface VehicleSuggestion {
  vehicleId: string;
  fleetNumber?: string;
  capacity: number;
  feasible: boolean;
  /** Chain deadhead km after a successful dry-run assign (sort key). */
  deadheadKm?: number;
  issues: ScheduleValidationIssue[];
}

/**
 * Rank fleet vehicles for manually assigning `jobId`.
 * Feasible first (by deadhead asc), then infeasible (capacity / conflicts).
 */
export function suggestVehiclesForJob(
  plan: DailySchedulePlan,
  jobId: string
): VehicleSuggestion[] {
  const job = (plan.jobs ?? []).find((j) => j.id === jobId);
  const fleet = plan.fleet ?? [];
  if (!job || fleet.length === 0) return [];

  const suggestions: VehicleSuggestion[] = [];

  for (const vehicle of fleet) {
    if (
      job.fleetOperatorId &&
      vehicle.fleetOperatorId !== job.fleetOperatorId
    ) {
      suggestions.push({
        vehicleId: vehicle.id,
        fleetNumber: vehicle.fleetNumber,
        capacity: vehicle.capacity,
        feasible: false,
        issues: [
          {
            code: 'FLEET_MISMATCH',
            params: {
              vehicle: vehicle.fleetNumber ?? vehicle.id,
            },
          },
        ],
      });
      continue;
    }

    if (vehicle.capacity < job.passengerCount) {
      suggestions.push({
        vehicleId: vehicle.id,
        fleetNumber: vehicle.fleetNumber,
        capacity: vehicle.capacity,
        feasible: false,
        issues: [
          {
            code: 'CAPACITY_SHORT',
            params: {
              vehicle: vehicle.fleetNumber ?? vehicle.id,
              capacity: vehicle.capacity,
              passengers: job.passengerCount,
            },
          },
        ],
      });
      continue;
    }

    const result = assignJobToVehicle(plan, jobId, vehicle.id);
    if (result.ok) {
      const chain = result.plan.vehicles.find((v) => v.vehicleId === vehicle.id);
      suggestions.push({
        vehicleId: vehicle.id,
        fleetNumber: vehicle.fleetNumber,
        capacity: vehicle.capacity,
        feasible: true,
        deadheadKm: chain?.validation.totalDeadheadKm ?? 0,
        issues: [],
      });
    } else {
      suggestions.push({
        vehicleId: vehicle.id,
        fleetNumber: vehicle.fleetNumber,
        capacity: vehicle.capacity,
        feasible: false,
        issues: result.issues,
      });
    }
  }

  suggestions.sort((a, b) => {
    if (a.feasible !== b.feasible) return a.feasible ? -1 : 1;
    if (a.feasible && b.feasible) {
      const d = (a.deadheadKm ?? 0) - (b.deadheadKm ?? 0);
      if (d !== 0) return d;
    }
    const an = a.fleetNumber ?? a.vehicleId;
    const bn = b.fleetNumber ?? b.vehicleId;
    return an.localeCompare(bn);
  });

  return suggestions;
}
